// src/hooks/notifications/useThreadNotificationIntegration.js
import { useCallback, useEffect } from "react";
import { useThreadContext } from "../../context/ThreadContext";
import { useSharedEventBridgeNotifications } from "./useSharedEventBridgeNotifications";
import { ThreadNotificationHandler } from "../../utils/notification/threadNotificationHandler";

/**
 * Hook that integrates thread notifications with the ThreadContext
 * Handles real-time updates to thread state when notifications are received
 */
export const useThreadNotificationIntegration = () => {
  const {
    currentThread,
    updateThreadMetadata,
    updateMessage,
    handleThreadUpdate,
  } = useThreadContext();

  // Handle message liked notification
  const handleMessageLiked = useCallback(
    (data) => {
      const { messageId, metadata } = data;

      if (!messageId) {
        return;
      }

      // Update message like count if we have the message in state
      updateMessage(messageId, {
        likeCount: metadata?.newLikeCount || undefined,
        likes: metadata?.likes || undefined,
        lastActivity: new Date().toISOString(),
      });
    },
    [updateMessage]
  );

  // Handle message replied notification
  const handleMessageReplied = useCallback(
    (data) => {
      const { messageId, metadata } = data;

      if (!messageId) {
        return;
      }

      // Update message reply count if we have the message in state
      updateMessage(messageId, {
        replyCount: metadata?.newReplyCount || undefined,
        lastActivity: new Date().toISOString(),
      });

      // Update thread message count if provided
      if (metadata?.threadMessageCount !== undefined) {
        updateThreadMetadata({
          messageCount: metadata.threadMessageCount,
          lastActivity: new Date().toISOString(),
        });
      }
    },
    [updateMessage, updateThreadMetadata]
  );

  // Handle thread notifications from EventBridge
  const handleThreadNotification = useCallback(
    (notification) => {
      const { data } = notification;

      if (!ThreadNotificationHandler.isThreadNotification(notification)) {
        return;
      }

      // Only update if this notification is for the current thread
      const notificationThreadId = data.threadId || data.metadata?.threadId;
      if (
        !notificationThreadId ||
        !currentThread ||
        currentThread.id !== notificationThreadId
      ) {
        return;
      }

      switch (data.type) {
        case "message.liked":
          handleMessageLiked(data);
          break;
        case "message.replied":
          handleMessageReplied(data);
          break;
        default:
        // Unknown notification type
      }

      // Trigger general thread update
      handleThreadUpdate({
        type: data.type,
        timestamp: Date.now(),
      });
    },
    [
      currentThread,
      handleThreadUpdate,
      handleMessageLiked,
      handleMessageReplied,
    ]
  );

  // Initialize EventBridge notifications with shared hook
  const eventBridgeHook = useSharedEventBridgeNotifications();

  // Process EventBridge notifications for thread updates
  useEffect(() => {
    if (
      eventBridgeHook.notifications &&
      eventBridgeHook.notifications.length > 0
    ) {
      // Process the most recent notification
      const latestNotification = eventBridgeHook.notifications[0];
      if (ThreadNotificationHandler.isThreadNotification(latestNotification)) {
        handleThreadNotification(latestNotification);
      }
    }
  }, [eventBridgeHook.notifications, handleThreadNotification]);

  return {
    ...eventBridgeHook,
    handleThreadNotification,
    isIntegrated: true,
  };
};

export default useThreadNotificationIntegration;
